import { Box, Button, Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { addDocument, getDocumentBlobUrl } from '@/api/document';

import { Drawer, DrawerProps } from '@/components/Layout/Drawer';

import { ERROR_CODE } from '@/constants/api';
import {
  TWO_SIDED_DOCUMENTS,
  TWO_SIDED_GUIDANCE_BY_TYPE,
} from '@/constants/document';

import { useConfirmation } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectOperationData } from '@/redux/operation/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import { DocumentSection, DocumentType } from '@/types/document';

import { getAPIErrorCode } from '@/utils/api';
import {
  getBackSideDocTypeName,
  groupLeaseAndLetterAsOther,
} from '@/utils/document';
import { downloadFile } from '@/utils/file';
import { getFileNameFromUrl } from '@/utils/string';

import { DocumentPreview } from '../components/DocumentPreview';
import { DocumentUploadStep } from './DocumentUploadStep';

import {
  DocumentCheckAction,
  StyledFileFrame,
} from '../components/components.styled';
import { DrawerContainer } from './Drawers.styled';

type DocumentUploadGuidance = {
  guidanceKey: string;
  guidanceParams?: Record<string, unknown>;
  descriptionKey?: string;
};

type RequiredDocumentUploadDrawerProps = {
  section: DocumentSection;
  type?: DocumentType;
  clientId?: string | number;
  onUploaded: () => void;
  uploadGuidance?: DocumentUploadGuidance;
  downloadPath?: string;
  downloadFileName?: string;
} & DrawerProps;

export function RequiredDocumentUploadDrawer({
  section,
  onClose,
  onUploaded,
  type,
  clientId,
  title,
  uploadGuidance,
  downloadPath,
  downloadFileName,
  ...props
}: RequiredDocumentUploadDrawerProps) {
  const { t } = useTranslate();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { showConfirmation } = useConfirmation();
  const userData = useAppSelector(selectUserData);
  const operationData = useAppSelector(selectOperationData);

  const { requiredDocumentBySection, user } = clientId
    ? operationData
    : userData;

  const [file, setFile] = useState<File | string>();
  const [isBackPhase, setIsBackPhase] = useState(false);
  const [showBackAlert, setShowBackAlert] = useState(false);
  const [frontSrc, setFrontSrc] = useState<File | string>();
  const [backSrc, setBackSrc] = useState<File | string>();
  const [showDualCheck, setShowDualCheck] = useState(false);
  const sectionType = section as string;
  const isSecondIdSection =
    section === DocumentSection.SecondID ||
    sectionType === DocumentSection.SecondCitizenshipID;
  const twoSidedSection = isSecondIdSection
    ? DocumentSection.SecondID
    : section;
  const isTwoSided = useMemo(
    () =>
      !!(
        section &&
        type &&
        TWO_SIDED_DOCUMENTS[twoSidedSection]?.includes(type)
      ),
    [section, type, twoSidedSection],
  );

  useEffect(() => {
    if (isBackPhase) {
      setShowBackAlert(true);
      const timer = setTimeout(() => {
        setShowBackAlert(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isBackPhase]);

  const {
    mutate: upload,
    isPending: isUploading,
    error: uploadError,
    reset: resetUpload,
  } = useMutationWithUserData({
    mutationFn: addDocument,
    onError: error => {
      if (getAPIErrorCode(error) === ERROR_CODE.DOCUMENT_SECURITY_VIOLATION) {
        resetUpload();
        showConfirmation({
          title: t('common.documentSecurityRejection.title'),
          description: t('common.documentSecurityRejection.description'),
          secondaryText: t('common.documentSecurityRejection.cancel'),
          primaryText: t('common.documentSecurityRejection.uploadNew'),
          onCancel: () => {
            setFile(undefined);
            onClose?.();
          },
          onConfirm: () => {
            setFile(undefined);
          },
        });
      }
    },
    onFinished: async () => {
      if (clientId) {
        await queryClient.invalidateQueries({
          queryKey: ['minor-holder-details-and-required-documents'],
        });
      }

      if (isTwoSided && !isBackPhase) {
        setFile(undefined);
        setIsBackPhase(true);
        return;
      }
      if (isTwoSided && isBackPhase) {
        setShowDualCheck(true);
        setFile(undefined);
        return;
      }
      onUploaded();
    },
  });

  const baseDocumentDetails = useMemo(
    () => (type ? requiredDocumentBySection[section]?.[type] : undefined),
    [requiredDocumentBySection, section, type],
  );

  const requiredDocumentDetails =
    (section === DocumentSection.ProofOfAddress &&
      type === DocumentType.Other &&
      groupLeaseAndLetterAsOther(
        user,
        requiredDocumentBySection[DocumentSection.ProofOfAddress]
          ?.Letter_Of_Employment,
      )) ||
    baseDocumentDetails;

  const {
    mutate: getForm,
    isPending: isDownloading,
    error: downloadError,
    reset: resetDownload,
  } = useMutation({
    mutationFn: async () => {
      const resolvedDownloadPath =
        downloadPath ?? requiredDocumentDetails?.downloadPath;
      if (!resolvedDownloadPath) {
        return;
      }

      const blobUrl = await getDocumentBlobUrl(resolvedDownloadPath);
      const resolvedDownloadFileName =
        downloadFileName ?? getFileNameFromUrl(resolvedDownloadPath);
      try {
        downloadFile(blobUrl, resolvedDownloadFileName);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    },
  });

  useEffect(() => {
    if (props.open) {
      resetDownload();
      resetUpload();
      setFile(undefined);
      setIsBackPhase(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const onDownload = () => {
    resetUpload();
    getForm();
  };

  const onUpload = (payload: File) => {
    if (!requiredDocumentDetails) return;

    resetUpload();
    // Keep previews for combined image check
    if (isTwoSided && !isBackPhase) {
      setFrontSrc(payload);
    }
    if (isTwoSided && isBackPhase) {
      setBackSrc(payload);
    }

    const uploadType =
      isTwoSided && isBackPhase
        ? (getBackSideDocTypeName(requiredDocumentDetails.name) as DocumentType)
        : requiredDocumentDetails.name;

    upload({
      file: payload,
      type: uploadType,
      section,
      clientId,
      operationId: userData?.user?.operationId,
    });
  };

  const onCancelPreview = () => {
    setFile(undefined);
  };

  const onConfirmDualCheck = () => {
    setShowDualCheck(false);
    onUploaded();
  };

  const onRetakeBackFromDualCheck = () => {
    setShowDualCheck(false);
    setBackSrc(undefined);
    setFrontSrc(undefined);
    setFile(undefined);
    setIsBackPhase(false);
  };
  const renderDocumentSelectorContent = useCallback(() => {
    if (!requiredDocumentDetails) return null;
    if (!file) {
      let guidanceKey: string | undefined;
      let guidanceParams: Record<string, unknown> | undefined;

      if (isTwoSided) {
        const guidance =
          TWO_SIDED_GUIDANCE_BY_TYPE[requiredDocumentDetails.name];
        guidanceKey = isBackPhase ? guidance?.back : guidance?.front;
      } else if (isSecondIdSection) {
        guidanceKey =
          'verification.documentUploadBox.secondID.nonPhotoIDGuidance';
        guidanceParams = {
          documentType: t(`documentType.${requiredDocumentDetails.name}`),
        };
      } else if (uploadGuidance) {
        guidanceKey = uploadGuidance.guidanceKey;
        guidanceParams = uploadGuidance.guidanceParams;
      }
      return (
        <DocumentUploadStep
          titleKey={`documentType.${requiredDocumentDetails.name}`}
          guidanceKey={guidanceKey}
          guidanceParams={guidanceParams}
          descriptionKey={
            uploadGuidance?.descriptionKey ??
            requiredDocumentDetails.description
          }
          showBackAlert={isBackPhase && showBackAlert}
          showDownload={
            !!(downloadPath ?? requiredDocumentDetails.downloadPath)
          }
          isDownloading={isDownloading}
          onDownload={onDownload}
          downloadError={downloadError}
          onSetFile={setFile}
        />
      );
    } else {
      return (
        <DocumentPreview
          title={t('common.documentCheck')}
          description={t('common.documentCheckNotice')}
          src={file}
          onCancel={onCancelPreview}
          onConfirm={onUpload}
          isPending={isUploading}
          pendingText={t('common.uploadingFile')}
          error={uploadError}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requiredDocumentDetails,
    file,
    isBackPhase,
    showBackAlert,
    isTwoSided,
    isUploading,
    uploadError,
    section,
    uploadGuidance,
  ]);

  return (
    <Drawer
      {...props}
      onClose={onClose}
      title={title}
      bgColor={(theme: Theme) => theme.palette.background.paper}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
    >
      <DrawerContainer>
        {showDualCheck ? (
          <>
            <Typography variant="h3" textAlign="center">
              {t('verification.stepConfirm.title')}
            </Typography>
            <Typography
              mb={1}
              variant="body2"
              color={theme.palette.grey[800]}
              textAlign="center"
            >
              {t('verification.stepConfirm.form.title')}
            </Typography>
            <Box display="flex" gap={2} flexDirection="column">
              <StyledFileFrame src={frontSrc as string} />
              <StyledFileFrame src={backSrc as string} />
            </Box>
            <DocumentCheckAction mt={3}>
              <Button
                variant="outlined"
                onClick={onRetakeBackFromDualCheck}
                fullWidth
              >
                {t('verification.stepConfirm.form.button.retake')}
              </Button>
              <Button onClick={onConfirmDualCheck} fullWidth>
                {t('verification.stepConfirm.form.button.confirm')}
              </Button>
            </DocumentCheckAction>
          </>
        ) : (
          renderDocumentSelectorContent()
        )}
      </DrawerContainer>
    </Drawer>
  );
}
